import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Receipt as ReceiptIcon,
  Warehouse as WarehouseIcon,
} from "@mui/icons-material";
import { healthService } from "../../services/health";
import { DatabaseHealthResponse } from "../../types/common/commonTypes";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import api from "../../services/api";

export const PageDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [healthData, setHealthData] = useState<DatabaseHealthResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const { user } = useAuth();

  const handleQuickAction = (route: string) => {
    navigate(route);
  };

  const run_so_report = async (email: string): Promise<any> => {
    const response = await api.post("/tasks/run_so_report", { email });
    return response.data;
  };

  useEffect(() => {
    const checkSystemHealth = async () => {
      try {
        setLoading(true);
        setError(null);

        // Test basic connection first
        const isConnected = await healthService.testConnection();
        if (!isConnected) {
          throw new Error("Unable to connect to API");
        }

        // Get detailed health information
        const health = await healthService.checkDatabaseHealth();
        setHealthData(health);
      } catch (err) {
        console.error("Health check failed:", err);
        setError(err instanceof Error ? err.message : "Health check failed");
      } finally {
        setLoading(false);
      }
    };

    checkSystemHealth();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Checking system status...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        <Typography variant="h6">System Connection Error</Typography>
        <Typography>{error}</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Please ensure the backend server is running at
          https://192.168.1.46:8000
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Welcome to Centese Nucleus
      </Typography>

      {!!statusMsg && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography>{statusMsg}</Typography>
        </Alert>
      )}

      {/* System Status */}
      <Box display="flex" flexDirection="column" gap={3}>
        <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={3}>
          <Box flex="1">
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">System Status</Typography>
                </Box>
                <Chip
                  label="API Connected"
                  color="success"
                  variant="outlined"
                  clickable={false}
                  onClick={() => {}}
                  sx={{ mb: 1 }}
                />
                {healthData && (
                  <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                      Status: {healthData.overall_status}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Database: {healthData.checks.connectivity.status}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last Check:{" "}
                      {new Date(healthData.timestamp).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          {healthData && (
            <Box flex="1">
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Database Health
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    <Box flex="1 1 45%">
                      <Typography variant="body2">
                        Connection Pool:{" "}
                        {healthData.checks.connection_pool.status}
                      </Typography>
                    </Box>
                    <Box flex="1 1 45%">
                      <Typography variant="body2">
                        Query Performance:{" "}
                        {healthData.checks.query_performance.status}
                      </Typography>
                    </Box>
                    <Box flex="1 1 45%">
                      <Typography variant="body2">
                        Pool Size:{" "}
                        {healthData.checks.connection_pool.metrics.pool_size}
                      </Typography>
                    </Box>
                    <Box flex="1 1 45%">
                      <Typography variant="body2">
                        Active Connections:{" "}
                        {
                          healthData.checks.connection_pool.metrics
                            .total_connections
                        }
                      </Typography>
                    </Box>
                    <Box flex="1 1 45%">
                      <Typography variant="body2">
                        Avg Query Time:{" "}
                        {healthData.checks.query_performance.metrics.average_query_time_ms.toFixed(
                          2,
                        )}
                        ms
                      </Typography>
                    </Box>
                    <Box flex="1 1 45%">
                      <Typography variant="body2">
                        Storage: {healthData.checks.storage.status}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>

        {/* Quick Actions */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click on any module below to navigate directly:
              </Typography>
              <Box mt={2}>
                {(user?.email === "rpreston@centese.com" ||
                  user?.is_superuser) && (
                  <Chip
                    label="Run Full Sales Report"
                    icon={<BusinessIcon />}
                    onClick={() => {
                      run_so_report(user.email)
                        .then((response) => {
                          if (response["status"] === "scheduled") {
                            setStatusMsg(
                              `Report scheduled to run at ${new Date(response["run_time"]).toLocaleDateString("en-US")}`,
                            );
                          }
                        })
                        .catch((e) => {
                          console.error(e);
                          setStatusMsg(
                            "Report failed to be scheduled. Notify IT.",
                          );
                        });
                    }}
                    clickable
                    color="primary"
                    variant="outlined"
                    sx={{ mr: 1, mb: 1 }}
                  />
                )}
                {/* <Chip
                  label="Vendor Management"
                  icon={<BusinessIcon />}
                  onClick={() => handleQuickAction("/vendors")}
                  clickable
                  color="primary"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label="Customer Management"
                  icon={<PeopleIcon />}
                  onClick={() => handleQuickAction("/customers")}
                  clickable
                  color="primary"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label="Item Catalog"
                  icon={<InventoryIcon />}
                  onClick={() => handleQuickAction("/items")}
                  clickable
                  color="primary"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label="Purchase Orders"
                  icon={<ShoppingCartIcon />}
                  onClick={() => handleQuickAction("/purchase-orders")}
                  clickable
                  color="primary"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label="Sales Orders"
                  icon={<ReceiptIcon />}
                  onClick={() => handleQuickAction("/sales-orders")}
                  clickable
                  color="primary"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label="Inventory Management"
                  icon={<WarehouseIcon />}
                  onClick={() => handleQuickAction("/inventory")}
                  clickable
                  color="primary"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                /> */}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
